import { useState, useEffect } from 'react';
import { Building2, Plus, Palette, Image as ImageIcon, Trash2, Mail, Search, X, Globe, Copy, Loader2 } from 'lucide-react';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';

export function OrganizationsConfig() {
  const { organizations, currentOrgId, setCurrentOrgId, currentUser } = useStore();
  const [isAdding, setIsAdding] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingOrg, setEditingOrg] = useState<any | null>(null);

  const [orgAdminsMap, setOrgAdminsMap] = useState<Record<string, any[]>>({});
  const [admins, setAdmins] = useState<any[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const emailVal = data.session?.user?.email || data.session?.user?.user_metadata?.email || localStorage.getItem('wfm_user_email');
      setUserEmail(emailVal || null);
    });
  }, []);

  const isSuperAdmin = userEmail?.toLowerCase().trim() === 'benmloukaamen@gmail.com' || 
    currentUser?.email === 'benmloukaamen@gmail.com';

  const loadAllAdmins = async () => {
    const { data, error } = await supabase
      .from('organization_admins')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (!error && data) {
      const map: Record<string, any[]> = {};
      data.forEach((r: any) => {
        if (!map[r.org_id]) map[r.org_id] = [];
        map[r.org_id].push(r);
      });
      setOrgAdminsMap(map);
    }
  };

  useEffect(() => {
    loadAllAdmins();
  }, [organizations]);

  const loadAdmins = async (orgId: string) => {
    setLoadingAdmins(true);
    const { data, error } = await supabase
      .from('organization_admins')
      .select('*')
      .eq('org_id', orgId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (!error && data) {
      setAdmins(data);
    }
    setLoadingAdmins(false);
  };

  useEffect(() => {
    if (editingOrg) {
      loadAdmins(editingOrg.id);
    }
  }, [editingOrg]);

  const handleCreate = async () => {
    if (!newOrgName.trim()) return;
    try {
      const { error } = await supabase.rpc('create_org_with_defaults', { p_name: newOrgName });
      if (error) throw error;
      setNewOrgName('');
      setIsAdding(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdate = async (id: string, updates: any) => {
    try {
      const { error } = await supabase.from('organizations').update(updates).eq('id', id);
      if (error) throw error;
      
      if (editingOrg && editingOrg.id === id) {
        const mappedUpdates: any = {};
        if (updates.name !== undefined) mappedUpdates.name = updates.name;
        if (updates.logo_url !== undefined) mappedUpdates.logoUrl = updates.logo_url;
        if (updates.settings !== undefined) mappedUpdates.settings = updates.settings;
        if (updates.allow_admin_settings !== undefined) mappedUpdates.allowAdminSettings = updates.allow_admin_settings;
        
        setEditingOrg((prev: any) => ({ ...prev, ...mappedUpdates }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddAdmin = async () => {
    if (!editingOrg) return;
    try {
      const emailVal = newAdminEmail.trim().toLowerCase();
      // Generate secure 16-byte hex token matching our schema defaults
      const tokenVal = crypto.randomUUID().replace(/-/g, '');

      // Upsert: if admin was previously soft-deleted, restore them with a new token
      const { error } = await supabase
        .from('organization_admins')
        .upsert({
          org_id: editingOrg.id,
          email: emailVal || null,
          email_confirmed: false,
          token: tokenVal,
          deleted_at: null
        }, { onConflict: 'org_id,email', ignoreDuplicates: false });

      if (error) throw error;
      setNewAdminEmail('');
      await loadAdmins(editingOrg.id);
      await loadAllAdmins();
    } catch (err: any) {
      alert("Erreur lors de l'ajout de l'administrateur : " + err.message);
    }
  };

  const handleRevokeAdmin = async (adminId: string) => {
    if (!confirm("Voulez-vous vraiment révoquer l'accès de cet administrateur ?")) return;
    try {
      const { error } = await supabase
        .from('organization_admins')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', adminId);

      if (error) throw error;
      await loadAdmins(editingOrg.id);
      await loadAllAdmins();
    } catch (err: any) {
      alert("Erreur lors de la révocation : " + err.message);
    }
  };

  const copyAdminLink = (token: string, id: string) => {
    const link = `${window.location.origin}/?org_token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredOrgs = organizations.filter(org => {
    const orgAdmins = orgAdminsMap[org.id] || [];
    const adminsMatch = orgAdmins.some(adm => adm.email?.toLowerCase().includes(searchQuery.toLowerCase()));
    return org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           org.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
           adminsMatch;
  });

  return (
    <div className="h-full overflow-y-auto custom-scrollbar bg-[#0A0B0E]">
      <div className="p-3.5 sm:p-6 md:p-8 max-w-6xl mx-auto pb-24">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4 relative z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2 flex items-center gap-2">
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400 flex-shrink-0" />
              <span className="truncate">Gestion des Organisations</span>
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
              Gérez les espaces de travail et les accès des organisateurs de manière centralisée.
            </p>
          </div>
          <button
            onClick={() => setIsAdding(true)}
            className="w-full md:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-all font-semibold shadow-lg shadow-indigo-600/20 text-xs sm:text-sm"
          >
            <Plus className="w-4 h-4 flex-shrink-0" />
            <span>Nouvelle Organisation</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une organisation par nom, ID ou email..."
              className="w-full bg-[#14161B] border border-[#2D3139] rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors placeholder-slate-500"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-500 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Add Organisation Overlay Card */}
        {isAdding && (
          <div className="mb-8 p-6 bg-[#14161B] border border-indigo-500/30 rounded-2xl shadow-xl animate-in fade-in slide-in-from-top-4">
            <h3 className="text-lg font-bold text-white mb-4">Créer un nouvel espace</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Nom de l'organisation (ex: Festival 2026)"
                className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  className="flex-1 sm:flex-none px-6 py-2 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-500 transition-all text-sm"
                >
                  Créer
                </button>
                <button
                  onClick={() => setIsAdding(false)}
                  className="flex-1 sm:flex-none px-6 py-2 bg-[#2D3139] text-slate-300 rounded-xl font-bold hover:bg-[#3D4149] transition-all text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compact Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrgs.map((org) => {
            const orgAdmins = orgAdminsMap[org.id] || [];
            const displayAdmins = orgAdmins
              .filter(adm => adm.email || adm.full_name)
              .map(adm => adm.full_name || adm.email) as string[];

            return (
              <div 
                key={org.id}
                className={`group bg-[#14161B]/80 border ${org.id === currentOrgId ? 'border-indigo-500/50' : 'border-[#2D3139]/60'} rounded-xl p-4 transition-all hover:border-indigo-500/30 flex items-center justify-between gap-4`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-[#0A0B0E] border border-[#2D3139]/60 flex items-center justify-center overflow-hidden shrink-0">
                    {org.logoUrl ? (
                      <img src={org.logoUrl} alt={org.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-sm truncate">{org.name}</h3>
                      {org.id === currentOrgId && (
                        <span className="px-1.5 py-0.2 bg-emerald-500/10 text-emerald-400 text-[8px] font-extrabold rounded border border-emerald-500/20 tracking-wider">
                          ACTIF
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">{org.id.split('-')[0]}...</p>
                    {displayAdmins.length > 0 ? (
                      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                        <span className="truncate">{displayAdmins.join(', ')}</span>
                      </p>
                    ) : (
                      <p className="text-[10px] text-slate-500 mt-1 italic">Aucun email configuré</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isSuperAdmin && org.id !== currentOrgId && (
                    <button
                      onClick={() => setCurrentOrgId(org.id)}
                      className="px-3 py-1.5 bg-emerald-600/15 border border-emerald-500/30 text-emerald-400 hover:text-white hover:bg-emerald-600 rounded-lg transition-all text-xs font-bold"
                    >
                      Activer
                    </button>
                  )}
                  <button
                    onClick={() => setEditingOrg(org)}
                    className="px-3.5 py-1.5 bg-[#1C1F26] border border-[#2D3139]/60 text-slate-300 hover:text-white hover:bg-[#2D3139] rounded-lg transition-all text-xs font-bold shrink-0"
                  >
                    Gérer
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filteredOrgs.length === 0 && (
          <div className="text-center py-16 bg-[#14161B]/40 rounded-2xl border border-dashed border-[#2D3139]/50">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">Aucune organisation trouvée</p>
            <p className="text-xs text-slate-500 mt-1">Essayez d'ajuster vos filtres ou créez-en une nouvelle.</p>
          </div>
        )}
      </div>

      {/* Full Configuration Modal overlay */}
      {editingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0B0E]/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#2D3139]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0A0B0E] border border-[#2D3139] flex items-center justify-center overflow-hidden shrink-0">
                  {editingOrg.logoUrl ? (
                    <img src={editingOrg.logoUrl} alt={editingOrg.name} className="w-full h-full object-contain" />
                  ) : (
                    <Building2 className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-white text-base">Configuration</h3>
                  <p className="text-[10px] text-slate-500 font-mono mt-0.5 truncate max-w-[200px]">{editingOrg.id}</p>
                </div>
              </div>
              <button 
                onClick={() => setEditingOrg(null)}
                className="p-1.5 rounded-lg bg-[#0A0B0E] hover:bg-[#1C1F26] text-slate-400 hover:text-white border border-[#2D3139]/60 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
              {/* Org Name */}
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">Nom de l'organisation</label>
                <input
                  type="text"
                  defaultValue={editingOrg.name}
                  onBlur={(e) => handleUpdate(editingOrg.id, { name: e.target.value })}
                  placeholder="Nom de l'organisation"
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">URL du Logo</label>
                <div className="relative">
                  <ImageIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    defaultValue={editingOrg.logoUrl}
                    onBlur={(e) => handleUpdate(editingOrg.id, { logo_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-10 pr-4 py-2.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
              </div>

              {/* Multi-Admin Management Section */}
              <div className="border border-[#2D3139] rounded-xl p-4 bg-[#0A0B0E]/50 space-y-4">
                <div>
                  <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Administrateurs de l'espace</h4>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Ajoutez plusieurs administrateurs et partagez leurs liens de validation d'accès.
                  </p>
                </div>

                {/* Admins List */}
                <div className="space-y-2">
                  {loadingAdmins ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                    </div>
                  ) : admins.length === 0 ? (
                    <div className="text-center py-4 text-xs text-slate-500 italic">
                      Aucun administrateur configuré.
                    </div>
                  ) : (
                    admins.map((adm) => (
                      <div key={adm.id} className="flex items-center justify-between gap-3 p-3 bg-[#14161B] border border-[#2D3139] rounded-xl">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-white truncate">
                              {adm.full_name || adm.email || "Lien d'invitation généré"}
                            </span>
                            <span className={`px-1.5 py-0.5 text-[8px] font-bold rounded uppercase ${
                              adm.email_confirmed
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            }`}>
                              {adm.email_confirmed ? 'Validé ✓' : 'En attente'}
                            </span>
                          </div>
                          {adm.full_name && adm.email && (
                            <p className="text-[10px] text-slate-500 truncate mt-0.5">{adm.email}</p>
                          )}
                          {!adm.email_confirmed && (
                            <p className="text-[9px] text-slate-500 truncate mt-0.5 select-all">
                              {`${window.location.origin}/?org_token=${adm.token.slice(0, 10)}...`}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => copyAdminLink(adm.token, adm.id)}
                            className="p-1.5 bg-[#0A0B0E] hover:bg-[#1C1F26] text-slate-400 hover:text-white border border-[#2D3139]/60 rounded-lg transition-all"
                            title="Copier le lien d'invitation"
                          >
                            {copiedId === adm.id ? (
                              <span className="text-[9px] text-emerald-400 font-bold px-1">Copié ✓</span>
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>

                          {isSuperAdmin && (
                            <button
                              onClick={() => handleRevokeAdmin(adm.id)}
                              className="p-1.5 bg-[#0A0B0E] hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-[#2D3139]/60 rounded-lg transition-all"
                              title="Révoquer l'administrateur"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Add Admin Form */}
                {isSuperAdmin && (
                  <div className="flex gap-2 pt-2 border-t border-[#2D3139]/50">
                    <input
                      type="email"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      placeholder="Inviter par email (optionnel)"
                      className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500"
                    />
                    <button
                      onClick={handleAddAdmin}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-all"
                    >
                      Ajouter
                    </button>
                  </div>
                )}
              </div>

              {/* Regional Settings */}
              <div className="bg-[#0A0B0E] border border-[#2D3139]/60 p-4 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-indigo-400" />
                    Paramètres Régionaux
                  </span>
                  {!isSuperAdmin && (
                    <span className="text-[8px] bg-slate-800 text-slate-400 border border-slate-700 px-2 py-0.5 rounded font-mono uppercase">
                      Lecture seule
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Currency */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">Devise des Offres</label>
                    <select
                      disabled={!isSuperAdmin}
                      value={editingOrg.settings?.currency || 'TND'}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newSettings = { ...(editingOrg.settings || {}), currency: val };
                        handleUpdate(editingOrg.id, { settings: newSettings });
                      }}
                      className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                      <option value="TND">TND (🇹🇳)</option>
                      <option value="EUR">EUR (🇪🇺)</option>
                      <option value="USD">USD (🇺🇸)</option>
                      <option value="GBP">GBP (🇬🇧)</option>
                    </select>
                  </div>

                  {/* Timezone */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">Fuseau Horaire</label>
                    <select
                      disabled={!isSuperAdmin}
                      value={editingOrg.settings?.timezone || 'Africa/Tunis'}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newSettings = { ...(editingOrg.settings || {}), timezone: val };
                        handleUpdate(editingOrg.id, { settings: newSettings });
                      }}
                      className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                      <option value="Africa/Tunis">Tunis (Africa/Tunis)</option>
                      <option value="Europe/Paris">Paris (Europe/Paris)</option>
                      <option value="Europe/Brussels">Bruxelles (Europe/Brussels)</option>
                      <option value="Europe/London">Londres (Europe/London)</option>
                      <option value="America/New_York">New York (America/New_York)</option>
                      <option value="Asia/Dubai">Dubaï (Asia/Dubai)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Theme Settings Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#0A0B0E] rounded-xl border border-[#2D3139]/60">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/10 rounded-lg">
                    <Palette className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">Onglet Apparence</p>
                    <p className="text-[9px] text-slate-500">Permettre à l'admin de changer le logo</p>
                  </div>
                </div>
                <button
                  onClick={() => handleUpdate(editingOrg.id, { allow_admin_settings: !editingOrg.allowAdminSettings })}
                  className={`w-10 h-6 rounded-full transition-all relative ${editingOrg.allowAdminSettings ? 'bg-indigo-600' : 'bg-[#2D3139]'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${editingOrg.allowAdminSettings ? 'left-5' : 'left-1'}`} />
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-[#0A0B0E] border-t border-[#2D3139]">
              <button 
                onClick={() => setEditingOrg(null)}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
